/**
 * <DownloadButton/> — visible click-to-download with state machine.
 *
 * Three states the user can SEE:
 *   idle        →  label + download icon
 *   downloading →  spinner + "Downloading…"
 *   done        →  ✓ tick + "Saved to Downloads" (resets after 3s)
 *   error       →  ✗ + the actual error message (click to retry)
 *
 * Mechanism:
 *   1. fetch() the URL with optional auth headers
 *   2. read response as Blob
 *   3. URL.createObjectURL(blob) — blob: URL is always same-origin so the
 *      browser's `download` attribute is *unconditionally* honoured
 *   4. mount a hidden <a>, click it, remove it, revoke the blob URL
 *
 * This is the canonical pattern used by GitHub, Google Drive, Dropbox.
 * It works on Chrome desktop, Chrome mobile, Safari, Firefox, Edge.
 *
 * Props:
 *   url, filename                  — required
 *   label                          — text shown in idle state
 *   headers                        — optional auth headers ({"X-Admin-Token": "..."})
 *   className, style, "data-testid", icon — passthrough
 */
import { useEffect, useRef, useState } from "react";
import { Download, Loader2, Check, AlertCircle } from "lucide-react";

export default function DownloadButton({
  url,
  filename,
  label,
  headers = {},
  className = "",
  style,
  icon = null,
  "data-testid": dataTestId,
  children,
}) {
  const [state, setState] = useState("idle"); // idle | downloading | done | error
  const [error, setError] = useState(null);
  const resetRef = useRef(null);

  useEffect(() => () => {
    if (resetRef.current) clearTimeout(resetRef.current);
  }, []);

  const onClick = async () => {
    if (state === "downloading") return;
    setState("downloading");
    setError(null);
    try {
      const r = await fetch(url, {
        method: "GET",
        headers,
        // Keep cookies out — admin endpoints use header/query auth, not session
        credentials: "omit",
        cache: "no-store",
      });
      if (!r.ok) {
        let msg = `HTTP ${r.status}`;
        try {
          const txt = await r.text();
          if (txt) msg += " — " + txt.slice(0, 160);
        } catch (_) { /* ignore */ }
        throw new Error(msg);
      }
      const rawBlob = await r.blob();
      if (!rawBlob || rawBlob.size === 0) throw new Error("Empty response");

      // CRITICAL: re-pack as application/octet-stream so Chrome and
      // Safari cannot preview the file inline (which is what makes a
      // "✓ Saved" success state appear without any file actually
      // landing in the Downloads folder when the user has Chrome's
      // built-in PDF viewer enabled). Octet-stream has no viewer, so
      // the browser is forced to honour the `download` attribute.
      const blob = new Blob([rawBlob], { type: "application/octet-stream" });
      const obj = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = obj;
      a.download = filename || "download";
      a.target = "_self";
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      // Safari + Firefox need the anchor to remain in the DOM briefly
      // after click(); revoking the blob URL too early aborts the
      // download mid-stream. Keep both alive for ~1.5 s.
      setTimeout(() => {
        a.remove();
        URL.revokeObjectURL(obj);
      }, 1500);
      setState("done");
      resetRef.current = setTimeout(() => setState("idle"), 3500);
    } catch (e) {
      setError(String(e.message || e));
      setState("error");
      resetRef.current = setTimeout(() => setState("idle"), 5000);
    }
  };

  const renderIcon = () => {
    if (state === "downloading") return <Loader2 size={14} className="ml-2 animate-spin" />;
    if (state === "done") return <Check size={14} className="ml-2" />;
    if (state === "error") return <AlertCircle size={14} className="ml-2" />;
    return icon || <Download size={14} className="ml-2" />;
  };

  const text = (() => {
    if (state === "downloading") return "Downloading…";
    if (state === "done") return "✓ Saved to Downloads";
    if (state === "error") return "Failed — click to retry";
    return label || children || "Download";
  })();

  const tone = state === "error"
    ? "bg-rose-600 text-white border-rose-700"
    : state === "done"
      ? "bg-emerald-600 text-white border-emerald-700"
      : null;

  return (
    <div className="inline-flex flex-col items-stretch gap-1">
      <button
        type="button"
        onClick={onClick}
        disabled={state === "downloading"}
        data-testid={dataTestId}
        data-download-state={state}
        title={error || ""}
        className={[
          "relative inline-flex items-center justify-center whitespace-nowrap",
          "font-medium h-10 px-4 py-2 text-sm rounded-none border",
          "transition-all duration-200 active:scale-[0.97] disabled:opacity-80",
          tone || "btn-ink hover:opacity-90",
          state === "downloading" && "cursor-wait",
          className,
        ].filter(Boolean).join(" ")}
        style={style}
      >
        {text}
        {renderIcon()}
      </button>
      {state === "error" && error && (
        <span className="text-[11px] text-rose-600 font-mono" data-testid={dataTestId ? `${dataTestId}-error` : undefined}>
          {error.slice(0, 120)}
        </span>
      )}
    </div>
  );
}
